import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { icons } from 'lucide-react';
import React, { useState } from 'react';

interface ShortcutGroup {
  title: string;
  shortcuts: Array<{
    label: string;
    keys: string[];
    description: string;
    iconName?: keyof typeof icons;
  }>;
}

const shortcutGroups: ShortcutGroup[] = [
  {
    title: 'Formatação',
    shortcuts: [
      {
        label: 'Negrito',
        keys: ['Mod', 'b'],
        description: 'Aplicar negrito ao texto',
        iconName: 'Bold',
      },
      {
        label: 'Itálico',
        keys: ['Mod', 'i'],
        description: 'Aplicar itálico ao texto',
        iconName: 'Italic',
      },
      {
        label: 'Sublinhado',
        keys: ['Mod', 'u'],
        description: 'Sublinhar texto',
        iconName: 'Underline',
      },
      {
        label: 'Link',
        keys: ['Mod', 'Alt', 'l'],
        description: 'Inserir ou editar link',
        iconName: 'Link',
      },
      {
        label: 'Abrir comandos',
        keys: ['Mod', 'k'],
        description: 'Abrir menu de comandos',
        iconName: 'Command',
      },
    ],
  },
  {
    title: 'Títulos',
    shortcuts: [
      {
        label: 'Título',
        keys: ['Mod', '1'],
        description: 'Aplicar título',
        iconName: 'Heading1',
      },
    ],
  },
  {
    title: 'Listas',
    shortcuts: [
      {
        label: 'Lista com marcadores',
        keys: ['Mod', 'Shift', 'l'],
        description: 'Criar lista com marcadores',
        iconName: 'List',
      },
      {
        label: 'Lista numerada',
        keys: ['Mod', 'Shift', 'o'],
        description: 'Criar lista numerada',
        iconName: 'ListOrdered',
      },
      {
        label: 'Lista de tarefas',
        keys: ['Mod', 'Alt', 't'],
        description: 'Criar lista de tarefas',
        iconName: 'ListTodo',
      },
    ],
  },
  {
    title: 'Inserir elementos',
    shortcuts: [
      {
        label: 'Imagem',
        keys: ['Mod', 'Shift', 'i'],
        description: 'Inserir imagem',
        iconName: 'Image',
      },
      {
        label: 'Tabela',
        keys: ['Mod', 'Shift', 'b'],
        description: 'Inserir tabela',
        iconName: 'Table',
      },
      {
        label: 'Gráfico',
        keys: ['Mod', 'Shift', 'g'],
        description: 'Inserir um gráfico personalizável',
        iconName: 'ChartPie',
      },
      {
        label: 'Colunas',
        keys: ['Mod', 'Shift', 'c'],
        description: 'Criar layout em colunas',
        iconName: 'Columns2',
      },
      {
        label: 'Bloco de destaque',
        keys: ['Mod', 'Shift', 'h'],
        description: 'Inserir bloco de destaque',
        iconName: 'Highlighter',
      },
      {
        label: 'Régua de tramitação',
        keys: ['Mod', 'Shift', 's'],
        description: 'Inserir régua de tramitação',
        iconName: 'Workflow',
      },
      {
        label: 'Régua horizontal',
        keys: ['Mod', 'Shift', 'r'],
        description: 'Inserir divisor horizontal',
        iconName: 'Minus',
      },
      {
        label: 'Citação em bloco',
        keys: ['Mod', 'Alt', 'q'],
        description: 'Inserir citação em bloco',
        iconName: 'Quote',
      },
    ],
  },
  {
    title: 'Navegação',
    shortcuts: [
      {
        label: 'Selecionar tudo',
        keys: ['Mod', 'a'],
        description: 'Selecionar todo o conteúdo',
        iconName: 'MousePointer',
      },
      {
        label: 'Desfazer',
        keys: ['Mod', 'z'],
        description: 'Desfazer última ação',
        iconName: 'Undo',
      },
      {
        label: 'Refazer',
        keys: ['Mod', 'y'],
        description: 'Refazer ação desfeita',
        iconName: 'Redo',
      },
    ],
  },
];

export const KeyboardShortcutsHelp: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Tooltip title="Atalhos de teclado">
        <Button
          variant="ghost"
          buttonSize="small"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <path
              d="M2 4C2 2.89543 2.89543 2 4 2H12C13.1046 2 14 2.89543 14 4V12C14 13.1046 13.1046 14 12 14H4C2.89543 14 2 13.1046 2 12V4Z"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M4 6H6M8 6H10M4 8H6M8 8H10M4 10H6M8 10H10M12 6H12.01M12 8H12.01M12 10H12.01"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-xs">Atalhos</span>
        </Button>
      </Tooltip>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg py-6 w-full max-w-[500px]">
            <div className="flex justify-between items-center mb-4 px-6">
              <h2 className="text-lg font-semibold">Atalhos de Teclado</h2>
              <Button
                variant="ghost"
                buttonSize="small"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </Button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto px-6">
              <div className="space-y-6">
                {shortcutGroups.map((group) => (
                  <div key={group.title}>
                    <h3 className="font-semibold text-sm text-gray-900 mb-3">
                      {group.title}
                    </h3>
                    <div className="space-y-2">
                      {group.shortcuts.map((shortcut) => (
                        <div
                          key={shortcut.label}
                          className="flex items-center justify-between py-2 border-b border-gray-100"
                        >
                          <div className="flex-1">
                            <div className="flex items-center">
                              {shortcut.iconName && (
                                <Icon
                                  name={shortcut.iconName}
                                  className="w-4 h-4 mr-2 text-gray-600"
                                />
                              )}
                              <div className="font-medium text-sm">
                                {shortcut.label}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500 ml-6">
                              {shortcut.description}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {shortcut.keys.map((key, index) => (
                              <React.Fragment key={key}>
                                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                                  {key === 'Mod'
                                    ? navigator.platform.includes('Mac')
                                      ? '⌘'
                                      : 'Ctrl'
                                    : key}
                                </kbd>
                                {index < shortcut.keys.length - 1 && (
                                  <span className="text-gray-400">+</span>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
